// @flow

import React from 'react';
import identityKey from 'identity-keys';
import AtomicInput from './AtomicInput';
import type { AtomicInputProps } from './AtomicInput';

type SelectInputProps<ValueType, NoValueType> = {
  noValue: NoValueType,
  options: Array<{|
    label: string,
    value: ValueType,
  |}>,
  placeholder?: string,
  keyMapper: (ValueType | NoValueType) => string,
} & AtomicInputProps<ValueType | NoValueType>;

export default class SelectInput<
  ValueType,
  NoValueType = void,
  AdditionalProps = {},
> extends AtomicInput<
  ValueType | NoValueType,
  string,
  SelectInputProps<ValueType, NoValueType> & AdditionalProps,
> {
  static defaultProps = {
    ...AtomicInput.defaultProps,
    required: false,
    noValue: undefined,
    options: [],
    placeholder: 'Please select…',
    keyMapper: identityKey,
  };

  valueToDomValue(value: ValueType | NoValueType): string {
    return this.props.keyMapper(value);
  }

  get _validValues(): Array<ValueType | NoValueType> {
    return [this.props.noValue, ...this.props.options.map(option => option.value)];
  }

  domValueToValue(rawValue: string): ValueType | NoValueType {
    const found = this._validValues.find(value => this.props.keyMapper(value) === rawValue);
    if (found === undefined) {
      return this.props.noValue;
    }
    return found;
  }

  hasValue() {
    return this.committedValue !== this.props.noValue;
  }

  render() {
    if (!this._validValues.includes(this.currentValue)) {
      throw new Error(
        `SelectInput's value of ${JSON.stringify(
          this.currentValue,
        )} is not a valid option - valid options are: ${JSON.stringify(this._validValues)}`,
      );
    }
    const isNoValueSelectedProps = {};
    const noValueKey = this.props.keyMapper(this.props.noValue);

    const { domValue } = this;
    if (domValue === noValueKey) {
      isNoValueSelectedProps.selected = true;
    }

    return (
      <select
        className={this.props.className}
        value={this.domValue}
        name={this.parent ? this.name : ''}
        onChange={ev => {
          this.inputDidChange(ev.target.value);
          this.inputDidCommitChange();
        }}
      >
        <option key={noValueKey} value={noValueKey} {...isNoValueSelectedProps}>
          {this.props.placeholder}
        </option>
        {this.props.options.map(option => {
          const selectedProps = {};
          const valueKey = this.props.keyMapper(option.value);
          if (domValue === valueKey) {
            selectedProps.selected = true;
          }
          return (
            <option key={valueKey} value={valueKey} {...selectedProps}>
              {option.label}
            </option>
          );
        })}
      </select>
    );
  }
}
